import React from 'react';
import { motion } from 'framer-motion';
import { Users, TrendingUp, DollarSign, Heart, Globe, CheckCircle } from 'lucide-react';
import AnimatedSection from '../AnimatedSection';

const FarmerImpactSection: React.FC = () => {
  return (
    <div className="py-24 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-green-100 px-4 py-2 rounded-full text-green-700 mb-8">
            <Heart className="h-5 w-5" />
            <span>Real-World Impact</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Transforming Farmers' Lives
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            AVN's platform is making a tangible difference in the lives of farmers across the globe through fair compensation, sustainable practices, and community support.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center mb-16">
            <AnimatedSection>
              <div className="grid grid-cols-2 gap-6">
                <img 
                  src="https://images.unsplash.com/photo-1597916829826-02e5bb4a54e0" 
                  alt="Happy farmer with harvested bananas" 
                  className="rounded-xl shadow-lg object-cover w-full h-full"
                />
                <img 
                  src="https://images.unsplash.com/photo-1464226184884-fa280b87c399" 
                  alt="Farmers working in a greenhouse" 
                  className="rounded-xl shadow-lg object-cover w-full h-full mt-8"
                />
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.3}>
              <div className="space-y-8">
                <h3 className="text-2xl font-bold">Empowering Farmer Communities</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-green-100 rounded-lg mt-1">
                      <DollarSign className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">25-50% Revenue Share</h4>
                      <p className="text-gray-600">By joining our ecosystem, farmers receive a guaranteed 25% of the final value of their products, with potential to earn up to 50%—far above the 6% typically earned in conventional supply chains.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-green-100 rounded-lg mt-1">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">45% Yield Increase</h4>
                      <p className="text-gray-600">Our regenerative farming techniques and AI-driven insights help increase yields by an average of 45%.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-green-100 rounded-lg mt-1">
                      <Users className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">5,000+ Farmers</h4>
                      <p className="text-gray-600">We are targeting 5,000 farmers across Uganda and beyond—already benefiting from our collaborative, data-driven network.</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                  <h4 className="font-bold flex items-center gap-2 mb-3">
                    <Globe className="h-5 w-5 text-avn-purple-600" />
                    <span>Sustainable Development Goals</span>
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">No Poverty</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Zero Hunger</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Climate Action</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Decent Work</span>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FarmerImpactSection;